import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import * as Types from './type-declerations';


const baseInitialState: Types.DialogsReduxSliceStateBaseEntry = {
    open: false,
    disableAutoFocus: false,
    disableEnforceFocus: false,
    disableEscapeKeyDown: false,
    disableBackdropClick: false,
    hideCloseButton: false,
    disablePortal: false,
    disableRestoreFocus: false,
    disableScrollLock: false,
    hideBackdrop: false,
    keepMounted: false,
    fullWidth: true,
    maxWidth: "sm",
}

const initialState: Types.DialogsReduxSliceState = {
    whatsNew: {
        ...baseInitialState,
        data: null,
        onClose: () => void 0,
    },
    quickOrders: {
        ...baseInitialState,
    },
    taskMoreInformation: {
        ...baseInitialState,
        data: {
            task: null,
            setTask: () => void 0,
            toggleTaskDoneStatus: () => void 0,
            page: null,
        },
    },
    upsertTask: {
        ...baseInitialState,
        data: {
            page: null,
            onSuccess: () => void 0,
            task: null,
            isNCRTask: false,
        },
    },
    upsertNote: {
        ...baseInitialState,
        data: {
            tagsList: [],
            onSuccess: () => void 0,
            note: null,
            defaultIsPrivate: true,
            titleSuggestions: [],
        },
    },
    welcomePanelTaskDialog: {
        ...baseInitialState,
    },
    removeConfirmation: {
        ...baseInitialState,
        onClose: () => void 0,
        data: {
            data: [],
            removedEntryTitle: null,
        },
    },
    confirmation: {
        ...baseInitialState,
        onClose: () => void 0,
        data: {
            title: "Confirmation",
            description: "Are you sure you want to proceed?",
            confirmButtonText: "Confirm",
            cancelButtonText: "Cancel",
            otherButtons: [],
        },
    },
    editBizForm: {
        ...baseInitialState,
        data: {
            formReference: undefined,
        },
    },
    viewInstallationGuide: {
        ...baseInitialState,
    }
}

const sliceName = 'newDialogs';

/**
 * Merges the given state object with the provided action.
 *
 * * if the value of [open] property of [action.payload] is set to false, resets the [type] property of the state.
 * @param state
 * @param action
 */
const mergePayloadWithState = (
    state: any,
    action: PayloadAction<Partial<Types.DialogsReduxSliceStateBaseEntry>>,
): Types.DialogsReduxSliceState => {
    const type = action.type.replace(`${sliceName}/`, '') as keyof Types.DialogsReduxSliceState;
    if (action?.payload?.open === false) {
        return {
            ...state,
            [type]: {
                ...state[type],
                ...initialState[type],
            },
        };
    }
    let data: any = undefined;
    if ('data' in action?.payload) {
        const initialData = (initialState[type] as any)?.data;
        switch (typeof action?.payload?.data) {
            case "object":
            case "undefined": {
                if (Array.isArray(action?.payload?.data)) {
                    data = [
                        ...((initialData as Array<any>) ?? []),
                        ...(action?.payload?.data ?? []),
                    ];
                    break;
                }
                data = {
                    ...(initialData ?? {}),
                    ...(action?.payload?.data ?? {}),
                };
                break;
            }
            default:
                data = action?.payload?.data ?? initialData;
        }
    }
    return {
        ...state,
        [type]: {
            ...state[type],
            ...action?.payload,
            data: data,
        },
    };
}

/**
 * The Redux state, action creators, types and reducers of the Dialogs slice.
 */
export const DialogsReduxSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        whatsNew: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateWhatsNewEntry>>
        ) => mergePayloadWithState(state, action),
        quickOrders: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateQuickOrdersEntry>>
        ) => mergePayloadWithState(state, action),
        taskMoreInformation: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateTaskMoreInformationEntry>>
        ) => mergePayloadWithState(state, action),
        upsertTask: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateUpsertTaskEntry>>
        ) => mergePayloadWithState(state, action),
        upsertNote: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateUpsertNoteEntry>>
        ) => mergePayloadWithState(state, action),
        welcomePanelTaskDialog: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateWelcomePanelTaskDialogEntry>>
        ) => mergePayloadWithState(state, action),
        removeConfirmation: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateRemoveConfirmationEntry>>
        ) => mergePayloadWithState(state, action),
        confirmation: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateConfirmationEntry>>
        ) => mergePayloadWithState(state, action),
        editBizForm: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateEditBizFormEntry>>
        ) => mergePayloadWithState(state, action),
        viewInstallationGuide: (
            state,
            action: PayloadAction<Partial<Types.DialogsReduxSliceStateViewInstallationGuideEntry>>
        ) => mergePayloadWithState(state, action),
    },
})

export * from './type-declerations';
export default DialogsReduxSlice;
